// Shared forgot-password modal for all 3 login pages.
// Always shows the same "if your email is on file, you'll get a link" message
// regardless of whether the email exists — same response shape, no enumeration.

import { useState } from "react";
import { Loader2, Mail, X, CheckCircle2 } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

type Audience = "investor" | "banker" | "admin" | "founder" | "mentor";

const AUDIENCE_CONFIG: Record<Audience, { label: string; endpoint: string; theme: "dark" | "light" }> = {
  investor: { label: "Investor", endpoint: "api/investor-portal/auth/forgot-password", theme: "light" },
  banker: { label: "Advisor", endpoint: "api/banker-portal/auth/forgot-password", theme: "light" },
  admin: { label: "Admin", endpoint: "api/admin/auth/forgot-password", theme: "dark" },
  founder: { label: "Founder", endpoint: "api/founder-portal/auth/forgot-password", theme: "dark" },
  mentor: { label: "Mentor", endpoint: "api/mentor-portal/auth/forgot-password", theme: "dark" },
};

interface ForgotPasswordModalProps {
  audience: Audience;
  open: boolean;
  initialEmail?: string;
  onClose: () => void;
}

export function ForgotPasswordModal({ audience, open, initialEmail, onClose }: ForgotPasswordModalProps) {
  const config = AUDIENCE_CONFIG[audience];
  const dark = config.theme === "dark";
  const [email, setEmail] = useState(initialEmail || "");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await fetch(`${BASE}${config.endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
    } catch {
      // Even on network error, show the success state — don't help attackers
      // distinguish "email not found" from "email send failed". We log on the
      // server side; the user-visible state is identical.
    } finally {
      setSubmitted(true);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setEmail("");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className={`w-full max-w-md rounded-2xl shadow-2xl border ${dark ? "bg-neutral-950 border-white/10 text-white" : "bg-white border-neutral-200 text-neutral-900"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between px-6 py-4 border-b ${dark ? "border-white/10" : "border-neutral-200"}`}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#FF5C00] flex items-center justify-center">
              <Mail className="w-4 h-4 text-white" />
            </div>
            <h2 className="font-serif text-lg font-bold">Reset password</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className={dark ? "text-white/50 hover:text-white" : "text-neutral-400 hover:text-neutral-700"}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className={`rounded-lg border p-4 flex items-start gap-3 ${dark ? "bg-emerald-950/40 border-emerald-700/40" : "bg-emerald-50 border-emerald-200"}`}>
              <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${dark ? "text-emerald-400" : "text-emerald-600"}`} />
              <div>
                <p className={`font-semibold text-sm ${dark ? "text-emerald-100" : "text-emerald-900"}`}>Check your inbox</p>
                <p className={`text-xs mt-1.5 leading-relaxed ${dark ? "text-emerald-200/80" : "text-emerald-800"}`}>
                  If an account exists for that email, we've sent a link to reset the password. The link expires in {audience === "admin" ? "1 hour" : audience === "founder" ? "24 hours" : "24 hours"}.
                </p>
                <button
                  type="button"
                  onClick={handleClose}
                  className={`mt-4 text-xs font-semibold ${dark ? "text-emerald-300 hover:text-emerald-200" : "text-emerald-700 hover:text-emerald-900"}`}
                >
                  Back to sign in →
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className={`text-sm leading-relaxed ${dark ? "text-white/70" : "text-neutral-600"}`}>
                Enter the email on your {config.label.toLowerCase()} account. If we find a match, we'll send a one-time link to reset your password.
              </p>
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${dark ? "text-white/60" : "text-neutral-700"}`}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  placeholder="you@company.com"
                  autoComplete="email"
                  className={`w-full px-3 py-2.5 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5C00]/40 focus:border-[#FF5C00] ${dark ? "bg-white/5 border border-white/10 text-white placeholder-white/30" : "bg-white border border-neutral-300 text-neutral-900"}`}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium border ${dark ? "border-white/10 text-white/70 hover:bg-white/5" : "border-neutral-200 text-neutral-700 hover:bg-neutral-50"}`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-[#FF5C00] hover:bg-[#E55300] text-white disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send reset link"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
